import debounce from "lodash/debounce";
import { handleCustomUDL } from "@utils/UDLUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  open: "open",
  pushLeft: "push_left",
  active: "active",
  current: "current",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class AnchorNavigationController {
  private el: HTMLElement;
  private abortController: AbortController;

  // Runtime state
  private initialized = false;
  private isClickScrolling = false;
  private clickScrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private anchorTimeout: ReturnType<typeof setTimeout> | null = null;
  // Manual cleanup needed for rAF cancellation (AbortController alone cannot cancel a pending rAF)
  private scrollCleanup: (() => void) | null = null;

  // Labels read from data attributes on the component root
  private readonly toggleButtonLabel: string;
  private readonly scrollButtonLabel: string;

  // Sections discovered by setVisibleSections — refreshed on each initAnchorNavigation call
  private visibleSections: HTMLElement[] = [];

  // Dynamically created elements — rebuilt on each initAnchorNavigation call
  private elComponent: HTMLElement | null = null;
  private elContainer: HTMLElement | null = null;
  private elList: HTMLUListElement | null = null;
  private listIconButton: HTMLButtonElement | null = null;
  private moveDownIconButton: HTMLButtonElement | null = null;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.toggleButtonLabel =
      el.getAttribute("data-toggle-button-label") ?? "Toggle anchor navigation";
    this.scrollButtonLabel =
      el.getAttribute("data-scroll-button-label") ?? "Move down";
  }

  init(): void {
    const { signal } = this.abortController;

    // viewportChange (Terrific) → debounced resize; rebuild when the breakpoint changes
    window.addEventListener(
      "resize",
      debounce(() => this.initAnchorNavigation(), 200),
      { signal },
    );

    // t.sync equivalent — initial build after all modules are ready
    this.initAnchorNavigation();
  }

  destroy(): void {
    this.scrollCleanup?.();
    this.scrollCleanup = null;
    this.abortController.abort();
    if (this.clickScrollTimeout !== null) clearTimeout(this.clickScrollTimeout);
    if (this.anchorTimeout !== null) clearTimeout(this.anchorTimeout);
  }

  // ─── Navigation init ─────────────────────────────────────────────────────────

  private initAnchorNavigation(): void {
    this.setVisibleSections();

    if (this.visibleSections.length > 1) {
      this.buildMoveDownButton();
      this.buildMarkup();
      this.hideList();
      this.initWaypoints();
      this.showListOnLoad();
    }
  }

  // ─── Build move-down button ──────────────────────────────────────────────────

  private buildMoveDownButton(): void {
    const arrowDownIcon = document.createElement("i");
    arrowDownIcon.className = "ci ci-navigation-arrow-down";

    const btn = document.createElement("button");
    btn.className = "nav-move-down";
    btn.setAttribute("aria-label", this.scrollButtonLabel);
    btn.append(arrowDownIcon);

    btn.addEventListener("click", () => {
      const activeLi = this.elContainer?.querySelector<HTMLLIElement>("li.active");
      if (!activeLi) return;
      const nextLi = activeLi.nextElementSibling as HTMLLIElement | null;
      nextLi?.querySelector<HTMLAnchorElement>("a")?.click();
    });

    this.moveDownIconButton = btn;
  }

  // ─── Build markup ────────────────────────────────────────────────────────────

  private buildMarkup(): void {
    // Remove any existing navigation component before rebuilding
    document.querySelectorAll(".comp-anchor-navigation").forEach((el) => el.remove());

    this.elComponent = document.createElement("div");
    this.elComponent.className = "comp-anchor-navigation";

    this.elContainer = document.createElement("div");
    this.elContainer.className = "nav-container";

    this.elList = document.createElement("ul");
    this.elList.className = "anchor-navigation-list";
    this.elList.id = "anchor-navigation-list";

    // Toggle button
    const listIcon = document.createElement("i");
    listIcon.className = "ci ci-navigation-anchor";
    this.listIconButton = document.createElement("button");
    this.listIconButton.className = "nav-toggle";
    this.listIconButton.setAttribute("aria-label", this.toggleButtonLabel);
    this.listIconButton.setAttribute("aria-expanded", "false");
    this.listIconButton.setAttribute("aria-controls", "anchor-navigation-list");
    this.listIconButton.append(listIcon);
    this.listIconButton.addEventListener("click", () => this.toggleList());

    this.elContainer.append(this.listIconButton);
    if (this.moveDownIconButton) this.elContainer.append(this.moveDownIconButton);

    // Build nav items from visible sections
    let firstItem = true;
    this.visibleSections.forEach((section) => {
      const id = section.id;
      const title = section.dataset.title ?? "";

      const label = document.createElement("span");
      label.className = "item-label";
      label.textContent = title;

      const bullet = document.createElement("span");
      bullet.className = "item-bullet";

      const navLink = document.createElement("a");
      navLink.href = `#${id}`;
      navLink.className = "clearfix";
      navLink.append(label, bullet);

      navLink.addEventListener("click", () => {
        // Close list on mobile (small) viewport
        if (document.documentElement.className.indexOf("small") >= 0) {
          this.hideList();
        }

        // Immediately highlight the clicked item (don't wait for scroll-spy)
        this.elContainer?.querySelectorAll("li").forEach((li) =>
          li.classList.remove(CLASSES.active),
        );
        const parentLi = navLink.parentElement;
        parentLi?.classList.add(CLASSES.active);

        // Suppress scroll-spy during the native smooth-scroll animation
        this.isClickScrolling = true;
        if (this.clickScrollTimeout !== null) clearTimeout(this.clickScrollTimeout);
        this.clickScrollTimeout = setTimeout(() => {
          this.isClickScrolling = false;
        }, 800);

        // Update "current" marker on the clicked item
        parentLi?.parentElement?.querySelectorAll("li").forEach((li) =>
          li.classList.remove(CLASSES.current),
        );
        parentLi?.classList.add(CLASSES.current);

        // UDL tracking (fires before native navigation)
        this.pushData(this.getCommonParameters(navLink));
      });

      const navItem = document.createElement("li");
      navItem.className = firstItem ? `item ${CLASSES.active}` : "item";
      navItem.append(navLink);
      firstItem = false;

      this.elList!.append(navItem);
    });

    this.elContainer.append(this.elList);
    this.elComponent.append(this.elContainer);
    document.body.append(this.elComponent);
  }

  // ─── Toggle / show / hide list ───────────────────────────────────────────────

  private toggleList(): void {
    const isOpen = this.elComponent?.classList.contains(CLASSES.open) ?? false;
    if (isOpen) {
      this.hideList();
    } else {
      this.showList();
    }

    this.pushData({
      nav_state: isOpen ? "close" : "open",
      page_url: window.location.href,
    });
  }

  private showList(): void {
    this.elComponent?.classList.add(CLASSES.open);
    document.body.classList.add(CLASSES.pushLeft);
    this.listIconButton?.setAttribute("aria-expanded", "true");
  }

  private hideList(): void {
    this.elComponent?.classList.remove(CLASSES.open);
    document.body.classList.remove(CLASSES.pushLeft);
    this.listIconButton?.setAttribute("aria-expanded", "false");
  }

  // ─── Show on load ─────────────────────────────────────────────────────────────

  private showListOnLoad(): void {
    const viewport = document.documentElement.className;
    if (!this.initialized) {
      this.initialized = true;
      if (viewport.indexOf("large") >= 0 || viewport.indexOf("medium") >= 0) {
        this.showList();
        this.anchorTimeout = setTimeout(() => this.hideList(), 4000);
      }
    } else {
      // Subsequent rebuilds (e.g. on resize): cancel any pending auto-hide
      if (this.anchorTimeout !== null) clearTimeout(this.anchorTimeout);
    }
  }

  // ─── Scroll spy (rAF-throttled) ───────────────────────────────────────────────

  private initWaypoints(): void {
    // Clean up previous scroll-spy before installing the new one
    this.scrollCleanup?.();
    this.scrollCleanup = null;

    const firstSection = this.visibleSections[0];
    if (!firstSection) return;

    const defaultOffset = 400;
    const bodyRect = document.body.getBoundingClientRect();
    const firstRect = firstSection.getBoundingClientRect();
    // outerHeight(true) equivalent — offsetHeight is close enough for offset calculation
    const firstHeight = firstSection.offsetHeight;
    const firstTop = firstRect.top - bodyRect.top;
    const firstBottom = firstTop + firstHeight;
    // outerHeight(true) of .comp-nav-bar — offsetHeight is accurate enough here
    const navBarHeight =
      document.querySelector<HTMLElement>(".comp-nav-bar")?.offsetHeight ?? 0;
    const firstOffset = navBarHeight - 10;
    const offset =
      firstBottom < defaultOffset
        ? firstTop + Math.trunc(firstHeight * 0.75)
        : defaultOffset;

    let rafId: number | null = null;
    let lastActiveId: string | null = null;

    /**
     * Single coordinated scroll-spy: picks the last section whose top is at or
     * above the trigger line — guarantees sequential transitions and correct
     * initial state (first item active at scroll-top).
     */
    const updateActiveSection = (): void => {
      // Skip while a click-initiated smooth scroll is in progress
      if (this.isClickScrolling) return;

      let activeId: string | null = null;
      let activeIndex = 0;

      this.visibleSections.forEach((section, i) => {
        const rect = section.getBoundingClientRect();
        const sectionOffset = i === 0 ? firstOffset : offset;
        if (rect.top <= sectionOffset) {
          activeId = section.id;
          activeIndex = i;
        }
      });

      // Default to first section when none have crossed the trigger line
      if (!activeId && this.visibleSections.length > 0) {
        activeId = this.visibleSections[0].id;
        activeIndex = 0;
      }

      // Only update DOM when the active section actually changes
      if (activeId && activeId !== lastActiveId) {
        lastActiveId = activeId;
        this.highlightAnchor(activeId);
        this.checkMoveDownVisibility(activeIndex + 1, this.visibleSections.length);
      }
    };

    const onScroll = (): void => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateActiveSection();
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    this.scrollCleanup = (): void => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // Initial evaluation
    requestAnimationFrame(() => updateActiveSection());
  }

  // ─── Move-down button visibility ─────────────────────────────────────────────

  private checkMoveDownVisibility(curr: number, total: number): void {
    if (!this.moveDownIconButton) return;
    // Show only when there is a next section AND we are on a small (mobile) viewport
    if (curr < total && document.documentElement.className.indexOf("small") > -1) {
      this.moveDownIconButton.style.display = "";
    } else {
      this.moveDownIconButton.style.display = "none";
    }
  }

  // ─── Anchor highlighting ──────────────────────────────────────────────────────

  private highlightAnchor(currentId: string): void {
    const currentAnchor = this.elContainer?.querySelector<HTMLAnchorElement>(
      `a[href="#${currentId}"]`,
    );
    if (!currentAnchor) return;
    this.elContainer?.querySelectorAll("li").forEach((li) =>
      li.classList.remove(CLASSES.active),
    );
    currentAnchor.parentElement?.classList.add(CLASSES.active);
  }

  // ─── Determine sections to spy on ────────────────────────────────────────────

  private setVisibleSections(): void {
    // Mirrors the original anchorCSSClasses string.
    // ":visible" (only applied to .comp-section) is handled as a post-filter below.
    const selector = [
      ".comp-section",
      ".comp-course-search",
      ".comp-lab-finder",
      ".comp-resource-search",
      ".comp-sales-force-search",
      ".comp-site-selector",
      ".comp-title-banner",
      ".comp-rotator-container",
      ".comp-html5module",
      ".comp-wow-slider",
      ".comp-course-search-v2",
      ".comp-course-detail-v2",
      ".course-detail__speakers",
      ".course-detail__description",
      ".course-detail__sessions",
      ".course-detail__journey",
      ".course-detail__contact",
      ".course-detail__downloads",
      ".course-detail__more-dates",
      ".course-detail__learning-targets",
      ".comp-listing",
      ".comp-rotator-section",
    ].join(", ");

    this.visibleSections = Array.from(
      document.querySelectorAll<HTMLElement>(selector),
    ).filter((el) => {
      // Exclude .comp-section elements that are descendants of .comp-rotator-section
      if (el.matches(".comp-section") && el.closest(".comp-rotator-section")) {
        return false;
      }
      // jQuery :visible equivalent for .comp-section: element must have non-zero dimensions
      if (el.matches(".comp-section") && el.offsetWidth === 0 && el.offsetHeight === 0) {
        return false;
      }
      // Must have a data-title attribute (both filter conditions from original)
      return !!el.dataset.title;
    });
  }

  // ─── UDL ─────────────────────────────────────────────────────────────────────

  private getCommonParameters(
    linkElement: HTMLAnchorElement,
  ): Record<string, string> {
    return { link_text: linkElement.innerText };
  }

  private pushData(params: Record<string, unknown>): void {
    handleCustomUDL("on_site_navigation", params);
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="anchor-navigation-controller"]')
      .forEach((el) => {
        new AnchorNavigationController(el).init();
      });
  }
}
